import {
    Box,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {motion} from "framer-motion";
import {Image} from "primereact/image";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import Error from "@/components/shared/error";
import React, {useEffect, useRef, useState} from "react";
import {useRouter} from "next/navigation";
import {useTranslation} from "react-i18next";
import {ERROR_PROPS, SELECT_OPTIONS} from "@/components/shared/type";
import {FIELDS, PASSWORD_ERROR, SERVER_ERROR_PROPS, USER_REGISTRATION} from "@/components/admin/login/types";
import {analyzeDataFromServer, emailRegex, formatDate, validatePassword} from "@/components/shared/client/utils";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {settingsSelectors, updateGlobalLoading} from "@/redux/slices/settings";
import LoadingButton from "@mui/lab/LoadingButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PasswordCheck from "@/components/admin/login/passwordCheck";
import {useProcessStudentRegistrationMutation} from "@/redux/rtk/students/shared";
import {SITE_SETTINGS_OPTIONS} from "@/app/admin/siteSettings/registrationForm/page";
import {defaultLoginBackgroundImage} from "@/components/constants";

const initialValue: USER_REGISTRATION = {
    fullName: "",
    email: "",
    password_1: "",
    password_2: "",
    firstName: "",
    lastName: "",
    gender: "male"
}

const button = {
    borderRadius: 2,
    py: 1.5,
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 600,
    boxShadow: "0 8px 16px rgba(0, 13, 255, 0.2)"
}

interface SHOW_PASSWORD {
    password_1: boolean;
    password_2: boolean
}

const StudentRegistration = ({setStep, fields, setFields, siteSettings}: FIELDS & {siteSettings: SITE_SETTINGS_OPTIONS | null}) => {
    const siteImages = useSelector((state) => settingsSelectors.getSiteImages(state), shallowEqual);
    const dispatch = useDispatch();
    const router = useRouter();
    const theme = useTheme();
    const {t} = useTranslation("login");
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [err, setErr] = useState<ERROR_PROPS | null>(null);
    const passwordRef1 = useRef(null);
    const passwordRef2 = useRef(null);
    const [formData, setFormData] = useState<USER_REGISTRATION>(initialValue);
    const [isInputError, setIsInputError] = useState<boolean>(true);
    const [showPassword, setShowPassword] = useState<SHOW_PASSWORD>({
        password_1: false,
        password_2: false,
    })
    const [passwordError, setPasswordError] = useState<PASSWORD_ERROR>({
        length: true,
        upperCase: true,
        number: true,
        special: true
    });

    const {theme: selectedTheme, webContext} = useSelector((state) => {
        const webContext = settingsSelectors.getWebContext(state);
        const theme = settingsSelectors.getTheme(state);
        return {webContext, theme};
    }, shallowEqual);

    const [processRegistration, {data, error, isLoading}] = useProcessStudentRegistrationMutation();

    const isPasswordMatch = (): boolean => {
        return !!formData?.password_1 && (formData?.password_1 === formData?.password_2)
    }

    const validateFields = () => {
        if (!formData?.password_1) return true;
        if (!formData?.password_2) return true;
        if (!formData?.email || !emailRegex.test(formData.email!)) return true;
        if (!formData?.fullName) return true;
        if (!formData?.fatherName) return true;
        if (!formData?.gender) return true;
        if (Object.values(passwordError).some(value => value)) return true;
        return !isPasswordMatch()
    }

    useEffect(() => {
        setIsInputError(validateFields())
    }, [formData])

    useEffect(() => {
        if (data?.data?.data) {
            const res = analyzeDataFromServer(data.data.data);
            if (res?.error) {
                setErr(res)
            }
            if (res?.redirect) {
                dispatch(updateGlobalLoading(true));
                router.push(res.redirectTo)
            }
        }
    }, [data]);

    const handleChange = (e: any) => {
        const {name, value} = e.target
        setFormData({
            ...formData,
            [name]: value,
        });
        if (name === "password_1") {
            setPasswordError(validatePassword(value));
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const temp = formData.fullName.split(" ");
        if(temp?.length > 0) {
            void processRegistration({
                ...formData,
                firstName: temp.length === 1 ? "Mr" : temp.length === 2 ? temp[0] : `${temp[0]} ${temp[1]}`,
                lastName: temp.length === 1 ? temp[0] : temp.length === 2 ? temp[1] : temp[2],
                password: formData?.password_1,
                textKeys: JSON.stringify({
                    accountRegistered: t("welcomeToMCJ"),
                    accountActivationCode: t('activateYourAccount')
                }),
                cycleId: fields.cycleId,
                dob: formatDate(new Date(fields.dateOfBirth!))
            })
        }
    }

    const genderOptions: SELECT_OPTIONS[] = [];

    const maleOption = {
        label: t("global:male"),
        value: "male"
    }

    const femaleOption = {
        label: t("global:female"),
        value: "female"
    }

    if(siteSettings?.gender === "male") {
        genderOptions.push(maleOption)
    }
    if(siteSettings?.gender === "female") {
        genderOptions.push(femaleOption)
    }
    if(siteSettings?.gender === "both") {
        genderOptions.push(maleOption, femaleOption)
    }

    const handleTogglePasswordVisibility = (type: number) => {
        const input = type === 1 ? passwordRef1.current : passwordRef2.current;
        // @ts-ignore
        const {selectionStart, selectionEnd} = input ?? {};
        setShowPassword(state => ({
            ...state,
            [`password_${type}`]: !showPassword[`password_${type}` as keyof SHOW_PASSWORD]
        }))
        setTimeout(() => {
            if (input) {
                // @ts-ignore
                input.setSelectionRange(selectionStart, selectionEnd);
            }
        }, 0);
    }

    const handleMouseDownPassword = (event: React.ChangeEvent<MouseEvent>) => {
        event.preventDefault();
    };

    return <Grid container className="df jcc aic we1 mhe1 hfc">
        <Grid
            size={{xs: false, md: 6, lg: 8}}
            className="df jcc aic"
            sx={{
                alignSelf: "stretch",
                ...!isMobile ? {
                    backgroundImage: siteImages?.images?.loginImage ? `url(${siteImages.dir}${siteImages.images.loginImage}1.webp)` : defaultLoginBackgroundImage,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundColor: (theme) => theme.palette?.backgrounds?.secondary
                } : {}
            }}
        >
        </Grid>
        <Grid className="jcc aic df" size={{xs: 12, md: 6, lg: 4}}
            // @ts-ignore
               sx={{background: theme => theme.palette?.backgrounds?.primary}}>

            <Grid container sx={{
                width: {
                    xs: "100%",
                    sm: "30rem",
                },
                // @ts-ignore
                background: theme => theme.palette?.backgrounds?.primary
            }}>
                <Box
                    className="he1 df fdc aic jcc we1"
                    sx={{
                        borderRadius: "10px",
                        p: 4
                    }}
                >
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5}}
                        style={{width: "100%"}}
                    >
                        {(webContext?.logo_1_url && webContext?.logo_2_url) ?
                            <Box className="df jcc">
                                <Image
                                    src={selectedTheme === 1 ? `${webContext.logo_2_url}1.webp` : `${webContext.logo_1_url}1.webp`}
                                    alt="Company Logo"
                                    width={'150'}
                                />
                            </Box> : null}
                        <Box sx={{mb: 2, mt: 2}}>
                            <Typography
                                variant="h4"
                                component="h1"
                                fontWeight="bold"
                                // @ts-ignore
                                sx={{color: (theme: Theme) => theme.palette?.texts?.main, mt: 2}}
                            >
                                {t('registerAnAccount')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {t('registerAnAccountText')}
                            </Typography>
                        </Box>

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={3}>

                                <TextField
                                    fullWidth
                                    label={t('global:name')}
                                    name="fullName"
                                    variant="outlined"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                />

                                <TextField
                                    fullWidth
                                    label={t('fatherName')}
                                    name="fatherName"
                                    variant="outlined"
                                    value={formData.fatherName}
                                    onChange={handleChange}
                                />

                                <FormControl fullWidth>
                                    <InputLabel id="simple-select-label">{t('gender')}</InputLabel>
                                    <Select
                                        name="gender"
                                        labelId="simple-select-label"
                                        id="simple-select"
                                        value={formData.gender}
                                        label={t('gender')}
                                        onChange={handleChange}
                                    >
                                        {genderOptions.map((rec) => {
                                            return <MenuItem value={rec.value}>{rec.label}</MenuItem>
                                        })}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label={t('emailAddress')}
                                    name="email"
                                    type="email"
                                    variant="outlined"
                                    value={formData.email}
                                    onChange={handleChange}
                                />

                                <TextField
                                    fullWidth
                                    label={t('global:password')}
                                    name="password_1"
                                    type={!showPassword?.password_1 ? "password" : "text"}
                                    variant="outlined"
                                    value={formData.password_1}
                                    onChange={handleChange}
                                    inputRef={passwordRef1}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {/*@ts-ignore*/}
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => handleTogglePasswordVisibility(1)}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {showPassword?.password_1 ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label={t('reTypePassword')}
                                    name="password_2"
                                    type={!showPassword?.password_2 ? "password" : "text"}
                                    variant="outlined"
                                    value={formData.password_2}
                                    onChange={handleChange}
                                    inputRef={passwordRef2}
                                    InputProps={{
                                        sx: {borderRadius: 2},
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {/*@ts-ignore*/}
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => handleTogglePasswordVisibility(2)}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {showPassword?.password_2 ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Grid container className="df we1" sx={{flexDirection: {xs: "column", md: "row"}}}>
                                    <Grid className="df flex5" sx={{p: 0.2}}>
                                        <LoadingButton
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            disabled={isLoading}
                                            onClick={() => setStep(1)}
                                            startIcon={<ChevronLeftIcon/>}
                                            sx={{
                                                ...button
                                            }}
                                        >
                                            {t("global:back")}
                                        </LoadingButton>
                                    </Grid>
                                    <Grid className="df flex5" sx={{p: 0.2}}>
                                        <LoadingButton
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            disabled={isInputError}
                                            loading={isLoading}
                                            sx={{
                                                ...button
                                            }}
                                        >
                                            {t('global:submit')}
                                        </LoadingButton>
                                    </Grid>
                                </Grid>

                            </Stack>
                        </form>

                        {(!!error || !!err) && <Error error={(error || err) as SERVER_ERROR_PROPS} timeout={10000}/>}

                        <PasswordCheck passwordError={passwordError} isPasswordMatch={isPasswordMatch}/>

                        <Grid container className="df jcc" sx={{mt: 2}}>
                            <Grid>{t('alreadyHaveAnAccount')}</Grid>
                            <Grid
                                className="fwb cp"
                                sx={{ml: 0.5, mr: 0.5, color: "primary.main"}}
                                onClick={() => setStep!(1)}
                            >
                                {t('signIn')}
                            </Grid>
                        </Grid>

                    </motion.div>
                </Box>
            </Grid>

        </Grid>
    </Grid>
}

export default StudentRegistration;